import React, { useEffect, useState } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem, Paper, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup,
    Tooltip, Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
    CRITICALITY_LABELS, ExecutionOverview, Risk, RiskCriticality, RiskKind, RiskStatus, RISK_STATUS_LABELS,
} from '../../../types/execution';
import executionService, { RiskPayload } from '../../../services/executionService';
import { CriticalityChip } from './StatusChip';
import { errorMessage, formatShortDate, formatTargetDate, toISODate } from './executionUtils';

type Notify = (message: string, severity?: 'success' | 'error' | 'info' | 'warning') => void;

interface RisksViewProps {
    projectId: number;
    overview: ExecutionOverview;
    reload: () => void;
    notify: Notify;
}

const KIND_LABELS: Record<RiskKind, string> = { RISK: 'Risco', ISSUE: 'Incidência' };

/** Datas-objetivo: as anteriores aparecem riscadas, a vigente em destaque. */
const TargetDates: React.FC<{ dates: string[] }> = ({ dates }) => (
    <Box>
        {dates.length === 0 && '-'}
        {dates.map((value, index) => (
            <Typography
                key={`${value}-${index}`}
                variant="body2"
                sx={index < dates.length - 1
                    ? { textDecoration: 'line-through', color: 'text.disabled' }
                    : { fontWeight: 600 }}
            >
                {formatTargetDate(value)}
            </Typography>
        ))}
    </Box>
);

interface RiskDialogProps {
    open: boolean;
    projectId: number;
    risk: Risk | null;
    defaultKind: RiskKind;
    overview: ExecutionOverview;
    onClose: () => void;
    onSaved: () => void;
    notify: Notify;
}

const RiskDialog: React.FC<RiskDialogProps> = ({ open, projectId, risk, defaultKind, overview, onClose, onSaved, notify }) => {
    const [form, setForm] = useState<RiskPayload>({});
    const [newTarget, setNewTarget] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setNewTarget('');
        setForm(risk ? {
            kind: risk.kind, code: risk.code, workstream_id: risk.workstream_id, scope_label: risk.scope_label,
            description: risk.description, impact_on: risk.impact_on, criticality: risk.criticality,
            owner_name: risk.owner_name, status: risk.status, identified_date: risk.identified_date,
            mitigation: risk.mitigation,
        } : {
            kind: defaultKind, criticality: 'MEDIUM', status: 'OPEN', identified_date: toISODate(new Date()),
            workstream_id: null, description: '',
        });
    }, [open, risk, defaultKind]);

    const set = (field: keyof RiskPayload) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm((prev) => ({ ...prev, [field]: e.target.value === '' ? null : e.target.value }));

    const handleSave = async () => {
        try {
            setSaving(true);
            const payload: RiskPayload = { ...form };
            if (newTarget) {
                if (risk) payload.new_target_date = newTarget;
                else payload.target_dates = [newTarget];
            }
            if (risk) {
                await executionService.updateRisk(risk.id, payload);
            } else {
                await executionService.createRisk(projectId, payload);
            }
            notify(form.kind === 'ISSUE' ? 'Incidência salva' : 'Risco salvo', 'success');
            onSaved();
            onClose();
        } catch (error) {
            notify(errorMessage(error, 'Erro ao salvar'), 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{risk ? `Editar ${risk.code}` : (defaultKind === 'RISK' ? 'Novo risco' : 'Nova incidência')}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={6} md={3}>
                        <TextField select fullWidth label="Tipo" value={form.kind || 'RISK'} onChange={set('kind')} disabled={!!risk}>
                            <MenuItem value="RISK">Risco</MenuItem>
                            <MenuItem value="ISSUE">Incidência</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <TextField fullWidth label="Código" value={form.code || ''} onChange={set('code')}
                            placeholder="Automático" helperText={risk ? undefined : 'Vazio = próximo código'} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField select fullWidth label="Frente" value={form.workstream_id ?? ''}
                            onChange={(e) => setForm((prev) => ({ ...prev, workstream_id: e.target.value === '' ? null : Number(e.target.value) }))}>
                            <MenuItem value="">(Transversal / várias)</MenuItem>
                            {overview.workstreams.map((ws) => <MenuItem key={ws.id} value={ws.id}>{ws.name}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Projeto (texto no relatório)" value={form.scope_label || ''} onChange={set('scope_label')}
                            helperText="Opcional. Ex.: quando o risco cobre mais de uma frente" />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth multiline minRows={2} label="Descrição" value={form.description || ''} onChange={set('description')} required />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Impacto em" value={form.impact_on || ''} onChange={set('impact_on')} placeholder="Ex.: Desenvolvimento" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Responsável" value={form.owner_name || ''} onChange={set('owner_name')} />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <TextField select fullWidth label="Criticidade" value={form.criticality || 'MEDIUM'} onChange={set('criticality')}>
                            {(Object.keys(CRITICALITY_LABELS) as RiskCriticality[]).map((c) => (
                                <MenuItem key={c} value={c}>{CRITICALITY_LABELS[c]}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <TextField select fullWidth label="Estado" value={form.status || 'OPEN'} onChange={set('status')}>
                            {(Object.keys(RISK_STATUS_LABELS) as RiskStatus[]).map((s) => (
                                <MenuItem key={s} value={s}>{RISK_STATUS_LABELS[s]}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <TextField fullWidth type="date" label="Data identificação" InputLabelProps={{ shrink: true }}
                            value={form.identified_date || ''} onChange={set('identified_date')} />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <TextField fullWidth label={risk?.target_dates.length ? 'Reprogramar data-objetivo' : 'Data-objetivo'}
                            value={newTarget} onChange={(e) => setNewTarget(e.target.value)}
                            placeholder="AAAA-MM-DD, Q4, TBC"
                            helperText={risk?.target_dates.length ? `Atual: ${formatTargetDate(risk.target_dates[risk.target_dates.length - 1])}` : undefined} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth multiline minRows={3} label="Ação, mitigação e resolução" value={form.mitigation || ''} onChange={set('mitigation')} />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button variant="contained" onClick={handleSave} disabled={!form.description?.trim() || saving}>Salvar</Button>
            </DialogActions>
        </Dialog>
    );
};

const RisksView: React.FC<RisksViewProps> = ({ projectId, overview, reload, notify }) => {
    const [kind, setKind] = useState<RiskKind>('RISK');
    const [statusFilter, setStatusFilter] = useState<'OPEN' | 'CLOSED' | 'ALL'>('OPEN');
    const [dialog, setDialog] = useState<{ open: boolean; risk: Risk | null }>({ open: false, risk: null });
    const [toDelete, setToDelete] = useState<Risk | null>(null);

    const source = kind === 'RISK' ? overview.risks : overview.issues;
    const criticalityOrder: Record<RiskCriticality, number> = { HIGH: 0, MEDIUM: 1, LOW: 2 };
    const rows = source
        .filter((r) => statusFilter === 'ALL' || r.status === statusFilter)
        .sort((a, b) => criticalityOrder[a.criticality] - criticalityOrder[b.criticality] || a.code.localeCompare(b.code));

    const handleDelete = async () => {
        if (!toDelete) return;
        try {
            await executionService.deleteRisk(toDelete.id);
            reload();
        } catch (error) {
            notify(errorMessage(error, 'Erro ao excluir'), 'error');
        } finally {
            setToDelete(null);
        }
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <Box display="flex" gap={2}>
                    <ToggleButtonGroup size="small" exclusive value={kind} onChange={(_, v) => v && setKind(v)}>
                        <ToggleButton value="RISK">Riscos ({overview.risks.length})</ToggleButton>
                        <ToggleButton value="ISSUE">Incidências ({overview.issues.length})</ToggleButton>
                    </ToggleButtonGroup>
                    <ToggleButtonGroup size="small" exclusive value={statusFilter} onChange={(_, v) => v && setStatusFilter(v)}>
                        <ToggleButton value="OPEN">Abertos</ToggleButton>
                        <ToggleButton value="CLOSED">Fechados</ToggleButton>
                        <ToggleButton value="ALL">Todos</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, risk: null })}>
                    {kind === 'RISK' ? 'Novo risco' : 'Nova incidência'}
                </Button>
            </Box>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Projeto</TableCell>
                            <TableCell sx={{ minWidth: 220 }}>Descrição</TableCell>
                            <TableCell>Impacto em</TableCell>
                            <TableCell>Criticidade</TableCell>
                            <TableCell>Responsável</TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell>Identificação</TableCell>
                            <TableCell>Data objetivo</TableCell>
                            <TableCell sx={{ minWidth: 240 }}>Ação, mitigação e resolução</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow><TableCell colSpan={11}><Typography color="text.secondary">Nenhum registro.</Typography></TableCell></TableRow>
                        )}
                        {rows.map((risk) => (
                            <TableRow key={risk.id}>
                                <TableCell sx={{ fontWeight: 600 }}>{risk.code}</TableCell>
                                <TableCell>{risk.scope_label || risk.workstream_name || '-'}</TableCell>
                                <TableCell sx={{ whiteSpace: 'pre-line' }}>{risk.description}</TableCell>
                                <TableCell>{risk.impact_on || '-'}</TableCell>
                                <TableCell><CriticalityChip criticality={risk.criticality} /></TableCell>
                                <TableCell>{risk.owner_name || '-'}</TableCell>
                                <TableCell>{RISK_STATUS_LABELS[risk.status]}</TableCell>
                                <TableCell>{formatShortDate(risk.identified_date)}</TableCell>
                                <TableCell><TargetDates dates={risk.target_dates} /></TableCell>
                                <TableCell sx={{ whiteSpace: 'pre-line' }}>{risk.mitigation || '-'}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    <Tooltip title="Editar">
                                        <IconButton size="small" onClick={() => setDialog({ open: true, risk })}><EditIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Excluir">
                                        <IconButton size="small" color="error" onClick={() => setToDelete(risk)}><DeleteIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <RiskDialog
                open={dialog.open}
                projectId={projectId}
                risk={dialog.risk}
                defaultKind={kind}
                overview={overview}
                onClose={() => setDialog({ open: false, risk: null })}
                onSaved={reload}
                notify={notify}
            />

            <Dialog open={!!toDelete} onClose={() => setToDelete(null)}>
                <DialogTitle>Confirmar exclusão</DialogTitle>
                <DialogContent>
                    <Typography>Excluir {toDelete?.code}? Para manter o histórico, prefira marcar como "Fechado".</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setToDelete(null)}>Cancelar</Button>
                    <Button color="error" onClick={handleDelete}>Excluir</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RisksView;
